import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDays, Settings, ChevronLeft, ChevronRight, Clock, Video, Phone, Loader2, CheckCircle2, AlertCircle, RefreshCw, Zap, X, Plus, Trash2, Smartphone, Mail, Cloud, MapPin, Users, Link2, FileText, Bot, Bell, PhoneCall } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
import { CALENDAR_EVENTS, CalendarEvent } from '../data/calendarEvents';
type ConnectionState = 'disconnected' | 'connecting' | 'success' | 'error' | 'connected';
type CalendarView = 'month' | 'day';
const EVENTS = CALENDAR_EVENTS;
interface CalendarScreenProps {
  onViewCall?: (callId: number) => void;
  initialEventId?: number | null;
  onInitialEventOpened?: () => void;
}
const EVENT_DAYS = [3, 7, 12, 14, 18, 24, 26, 27];
const fadeSlide = {
  initial: {
    opacity: 0,
    y: 16
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -16
  },
  transition: {
    duration: 0.3,
    ease: [0.4, 0, 0.2, 1]
  }
};
const TODAY_DAY = 24;
type CalendarProvider = 'google' | 'outlook' | 'icloud' | 'phone' | 'yahoo';
interface ConnectedAccount {
  id: string;
  provider: CalendarProvider;
  label: string;
  detail: string;
}
const ALL_PROVIDERS: {
  id: CalendarProvider;
  label: string;
  detail: string;
}[] = [{
  id: 'google',
  label: 'Google Calendar',
  detail: 'Connect a Google account'
}, {
  id: 'outlook',
  label: 'Outlook / Microsoft 365',
  detail: 'Office and personal accounts'
}, {
  id: 'icloud',
  label: 'iCloud Calendar',
  detail: 'Apple ID calendar sync'
}, {
  id: 'phone',
  label: 'Phone Calendar',
  detail: 'Local device calendar'
}, {
  id: 'yahoo',
  label: 'Yahoo Calendar',
  detail: 'Connect a Yahoo account'
}];
const PROVIDER_EMAILS: Record<CalendarProvider, string> = {
  google: 'alex.founder@gmail.com',
  outlook: 'alex@outlook.com',
  icloud: 'alex@icloud.com',
  phone: 'iPhone Local',
  yahoo: 'alex@yahoo.com'
};
export function CalendarScreen({
  onViewCall,
  initialEventId,
  onInitialEventOpened
}: CalendarScreenProps) {
  const {
    isLight
  } = useTheme();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [provider, setProvider] = useState<'google' | 'outlook' | null>(null);
  const [view, setView] = useState<CalendarView>('day');
  const [hasCalendarData, setHasCalendarData] = useState(false);
  const [selectedDate, setSelectedDate] = useState<number>(TODAY_DAY);
  const [showSettings, setShowSettings] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [addingProvider, setAddingProvider] = useState<CalendarProvider | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [reminderIds, setReminderIds] = useState<number[]>([]);
  React.useEffect(() => {
    if (initialEventId === null || initialEventId === undefined) return;
    const event = EVENTS.find(candidate => candidate.id === initialEventId);
    if (event) {
      setHasCalendarData(true);
      setView('day');
      setSelectedDate(event.day);
      setSelectedEvent(event);
    }
    onInitialEventOpened?.();
  }, [initialEventId, onInitialEventOpened]);
  const [reminderToast, setReminderToast] = useState<string | null>(null);
  const toggleReminder = (event: CalendarEvent) => {
    setReminderIds(prev => {
      const isSet = prev.includes(event.id);
      if (isSet) {
        setReminderToast(`Reminder removed for "${event.title}"`);
        return prev.filter(id => id !== event.id);
      }
      setReminderToast(`We'll remind you 15 min before "${event.title}"`);
      return [...prev, event.id];
    });
    setTimeout(() => setReminderToast(null), 2800);
  };
  const handleAddAccount = (p: CalendarProvider) => {
    setAddingProvider(p);
    setTimeout(() => {
      setConnectedAccounts(prev => [...prev, {
        id: `${p}-${Date.now()}`,
        provider: p,
        label: ALL_PROVIDERS.find(x => x.id === p)!.label,
        detail: PROVIDER_EMAILS[p]
      }]);
      setAddingProvider(null);
    }, 1400);
  };
  const handleRemoveAccount = (id: string) => {
    setConnectedAccounts(prev => {
      const next = prev.filter(a => a.id !== id);
      if (next.length === 0) {
        setConnectionState('disconnected');
        setShowSettings(false);
      }
      return next;
    });
  };
  const handleConnect = (p: 'google' | 'outlook') => {
    setProvider(p);
    setConnectionState('connecting');
    setTimeout(() => {
      setConnectionState('success');
      setConnectedAccounts(prev => prev.some(a => a.provider === p) ? prev : [...prev, {
        id: `${p}-${Date.now()}`,
        provider: p,
        label: ALL_PROVIDERS.find(x => x.id === p)!.label,
        detail: PROVIDER_EMAILS[p]
      }]);
      setTimeout(() => setConnectionState('connected'), 1400);
    }, 2200);
  };
  const handleRetry = () => {
    if (provider) handleConnect(provider);
  };
  // ─── Disconnected ────────────────────────────────────────
  const renderDisconnected = () => <motion.div key="disconnected" {...fadeSlide} className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-1108" data-mp="2wumuw6srczxv">
      <div className="relative mb-10" data-id="element-1109" data-mp="31sofiw5u17bg">
        <div className={`absolute inset-0 rounded-full blur-3xl scale-150 animate-pulse pointer-events-none ${isLight ? 'bg-vexu/20' : 'bg-vexu/15'}`} data-id="element-1110" data-mp="2n4uzjacu42sc" />
        <div className={`relative w-24 h-24 rounded-[28px] border flex items-center justify-center transition-colors duration-500 ${isLight ? 'bg-white border-slate-200 shadow-xl shadow-vexu/10' : 'bg-dark-card border-white/10 shadow-2xl shadow-black/60'}`} data-id="element-1111" data-mp="2s1ldns52wob4">
          <CalendarDays className={`w-11 h-11 ${isLight ? 'text-vexu-600' : 'text-white'}`} strokeWidth={1.6} data-id="element-1112" data-mp="3ufftea96nvsc" />
        </div>
      </div>

      <h2 className={`text-[22px] font-bold mb-2 tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1113" data-mp="3kh0c0yzyk0w1">
        Connect Your Calendar
      </h2>
      <p className={`text-sm leading-relaxed mb-10 max-w-[260px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1114" data-mp="3bh3iok2midun">
        Let Vexu.ai manage your schedule, book meetings from calls, and keep you
        organized.
      </p>

      <div className="w-full space-y-3 max-w-xs" data-id="element-1115" data-mp="31sbrie5dzxks">
        <button onClick={() => handleConnect('google')} className={`w-full h-[52px] rounded-2xl backdrop-blur-lg border flex items-center justify-center gap-3 font-medium active:scale-[0.98] transition-all ${isLight ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.08] text-white hover:bg-white/[0.08]'}`} data-id="element-1116" data-mp="2ws7cyto1hcx7">
          <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-1117" data-mp="13btbedmuv67x">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-1118" data-mp="1878hgjemfxts" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-1119" data-mp="1h7yetrjj5w9m" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-1120" data-mp="oku2t2m0shoc" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-1121" data-mp="yboqmbewzpbw" />
          </svg>
          Continue with Google
        </button>

        <button onClick={() => handleConnect('outlook')} className={`w-full h-[52px] rounded-2xl backdrop-blur-lg border flex items-center justify-center gap-3 font-medium active:scale-[0.98] transition-all ${isLight ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.08] text-white hover:bg-white/[0.08]'}`} data-id="element-1122" data-mp="2vcxs5mjyk4mx">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" data-id="element-1123" data-mp="3e9z1fjkjoqwo">
            <path d="M11.5 24L0 20.5V3.5L11.5 0V24Z" fill="#0078D4" data-id="element-1124" data-mp="39c7aosvmzau2" />
            <path d="M24 5.5V18.5C24 19.33 23.33 20 22.5 20H13V4H22.5C23.33 4 24 4.67 24 5.5Z" fill="#0078D4" opacity="0.7" data-id="element-1125" data-mp="39a36e9tts9t9" />
            <ellipse cx="6" cy="12" rx="3.5" ry="4.5" fill="white" data-id="element-1126" data-mp="3j2inz617mwdm" />
          </svg>
          Continue with Outlook
        </button>
      </div>
    </motion.div>;
  // ─── Connecting ──────────────────────────────────────────
  const renderConnecting = () => <motion.div key="connecting" {...fadeSlide} className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-1127" data-mp="fijgtdkrkec8">
      <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-1128" data-mp="kfte1fee9uam">
        <div className="relative w-32 h-32 mb-8" data-id="element-1129" data-mp="6k7h8m1x667n">
          <motion.div animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.1, 0.3]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-1130" data-mp="aiyi949ia801" />
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-1131" data-mp="3ixoyikkmk4oi" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-1132" data-mp="1424ejeny7gbm">
            <motion.div animate={{
            scale: [1, 1.1, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} data-id="element-1133" data-mp="192s3lusm9n37">
              <CalendarDays className="w-10 h-10 text-vexu" data-id="element-1134" data-mp="102lx4svc5ndj" />
            </motion.div>
          </div>
        </div>
        <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1135" data-mp="1nromkmw7okil">
          Syncing Calendar
        </p>
        <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1136" data-mp="1e1v9fhgf7xc8">
          Securely importing your events...
        </p>
      </div>
    </motion.div>;
  // ─── Success ─────────────────────────────────────────────
  const renderSuccess = () => <motion.div key="success" {...fadeSlide} className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-1137" data-mp="2bgr13lvjtx8v">
      <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20
    }} className={`w-20 h-20 rounded-full border flex items-center justify-center mb-6 transition-colors ${isLight ? 'bg-vexu/10 border-vexu/30 shadow-[0_0_30px_rgba(0,200,83,0.2)]' : 'bg-vexu/15 border-vexu/25 shadow-[0_0_40px_rgba(0,200,83,0.15)]'}`} data-id="element-1138" data-mp="2ku87fk4zkhtd">
        <CheckCircle2 className={`w-10 h-10 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1139" data-mp="1xw4yx6ahfjhn" />
      </motion.div>
      <h3 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1140" data-mp="2bw606munssxn">
        Connected
      </h3>
      <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1141" data-mp="3n9ro64os8ohl">
        Your calendar is synced with Vexu.ai
      </p>
    </motion.div>;
  // ─── Error ───────────────────────────────────────────────
  const renderError = () => <motion.div key="error" {...fadeSlide} className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-1142" data-mp="3qb36ttdnszea">
      <div className={`w-20 h-20 rounded-full border flex items-center justify-center mb-6 transition-colors ${isLight ? 'bg-red-50 border-red-200' : 'bg-red-500/10 border-red-500/20'}`} data-id="element-1143" data-mp="36x1r2mkl7cr">
        <AlertCircle className={`w-10 h-10 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-1144" data-mp="36nx4rp1m6rtn" />
      </div>
      <h3 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1145" data-mp="3blypeeeouz78">
        Connection Failed
      </h3>
      <p className={`text-sm mb-6 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1146" data-mp="3gjqel0ny877q">
        We couldn't connect to your calendar. Please try again.
      </p>
      <Button variant="secondary" onClick={handleRetry} icon={<RefreshCw className="w-4 h-4" data-id="element-1148" data-mp="2svgwo9tuw3lv" />} data-id="element-1147" data-mp="2nwcqzt654o0a">
        Retry Connection
      </Button>
    </motion.div>;
  // ─── Connected: Calendar ─────────────────────────────────
  const renderConnected = () => <motion.div key="connected" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="flex-1 flex flex-col h-full" data-id="element-1149" data-mp="2geoa7k0e1y6g">
      {/* Header */}
      <div className="px-6 pt-6 pb-2" data-id="element-1150" data-mp="26efcu2unu6ph">
        <div className="flex justify-between items-center mb-5" data-id="element-1151" data-mp="26hjp7nia48r8">
          <h1 className={`text-2xl font-bold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1152" data-mp="22en9c4k4iqqz">
            Calendar
          </h1>
          <button onClick={() => setShowSettings(true)} className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.08] hover:bg-white/[0.08]'}`} data-id="element-1153" data-mp="1sniur5yd17wo">
            <Settings className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1154" data-mp="1epc5ufbe2i95" />
          </button>
        </div>

        {/* View toggle */}
        <div className={`flex p-1 border rounded-xl mb-5 transition-colors ${isLight ? 'bg-slate-200/50 border-slate-200' : 'bg-white/[0.04] border-white/[0.06]'}`} data-id="element-1155" data-mp="28ejnc1etwosl">
          {(['day', 'month'] as const).map(v => <button key={v} onClick={() => setView(v)} className={`relative flex-1 py-1.5 text-sm font-medium rounded-lg capitalize transition-all ${view === v ? isLight ? 'text-slate-900 shadow-sm' : 'text-white' : isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-1156" data-mp="1ymnmiilcykko">
              {view === v && <motion.div layoutId="calViewToggle" className={`absolute inset-0 rounded-lg border ${isLight ? 'bg-white border-slate-200' : 'bg-white/[0.08] border-white/[0.06]'}`} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 35
          }} data-id="element-1157" data-mp="1fpuu0ok0lnre" />}
              <span className="relative z-10" data-id="element-1158" data-mp="ug9rqu73nma5">{v}</span>
            </button>)}
        </div>

        {/* Date nav */}
        <div className="flex items-center justify-between mb-4" data-id="element-1159" data-mp="13f3ja65kpmgu">
          <h2 className={`text-base font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1160" data-mp="13dk4yl3h9lhp">
            {view === 'day' ? selectedDate === TODAY_DAY ? `Today, Feb ${selectedDate}` : `Feb ${selectedDate}, 2026` : 'February 2026'}
          </h2>
          <div className="flex gap-1.5" data-id="element-1161" data-mp="1r2bnslv6grht">
            <button className={`p-1.5 rounded-lg border transition-colors ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.06] hover:bg-white/[0.08]'}`} data-id="element-1162" data-mp="1m6nz07kly0cu">
              <ChevronLeft className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1163" data-mp="3nyslh3jc85g6" />
            </button>
            <button className={`p-1.5 rounded-lg border transition-colors ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.06] hover:bg-white/[0.08]'}`} data-id="element-1164" data-mp="1wsebofnoksto">
              <ChevronRight className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1165" data-mp="25v5oojw7fwnf" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-28" data-id="element-1166" data-mp="2fpxq5yswr3nh">
        <AnimatePresence mode="wait" data-id="element-1167" data-mp="2ki8nctw9kbcc">
          {!hasCalendarData ? <motion.div key="empty" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3
        }} data-id="element-1168" data-mp="2phbtyirw6pmf">
              {view === 'day' /* Empty Day View */ ? <div className="flex flex-col items-center justify-center text-center pt-16" data-id="element-1169" data-mp="hxxypt4dc6e2">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1170" data-mp="dwcchh5y2fr7">
                    <CalendarDays className={`w-7 h-7 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1171" data-mp="15kwawelggs66" />
                  </div>
                  <p className={`text-sm font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1172" data-mp="wsasizlqknhv">
                    No events today
                  </p>
                  <p className={`text-xs leading-relaxed max-w-[220px] mb-5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1173" data-mp="1paggzha5rmai">
                    Your calendar is synced but there are no events scheduled
                    for today.
                  </p>
                  <button onClick={() => setHasCalendarData(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-vexu text-white text-sm font-medium hover:bg-vexu-600 transition-colors" data-id="element-1174" data-mp="xm3rv38jt7wt">
                    <Zap className="w-4 h-4" data-id="element-1175" data-mp="1bllgnilugl66" />
                    Simulate Events
                  </button>
                </div> /* Empty Month View — show grid but no event dots */ : <div data-id="element-1176" data-mp="12kx0ah8za8q4">
                  <div className={`backdrop-blur-xl border rounded-2xl p-4 ${isLight ? 'bg-white/80 border-slate-200/60 shadow-sm' : 'bg-white/[0.03] border-white/[0.07]'}`} data-id="element-1177" data-mp="12looh708x46d">
                    <div className="grid grid-cols-7 gap-0.5 mb-1" data-id="element-1178" data-mp="3na313on5jqa2">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} className={`text-center text-[11px] font-bold py-2 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1179" data-mp="3raeimsk7xahb" data-mpi={i}>
                          {d}
                        </div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5" data-id="element-1180" data-mp="3orruybh9fv2j">
                      {Array.from({
                  length: 35
                }).map((_, i) => {
                  const day = i + 1;
                  const inMonth = day >= 1 && day <= 28;
                  const isToday = day === TODAY_DAY;
                  const isSelected = day === selectedDate && inMonth;
                  return <button key={i} onClick={() => inMonth && setSelectedDate(day)} disabled={!inMonth} className={`
                              aspect-square flex flex-col items-center justify-center rounded-xl relative cursor-pointer transition-all
                              ${isSelected ? 'bg-vexu text-white shadow-[0_4px_12px_rgba(0,200,83,0.3)] scale-[1.02]' : isToday ? isLight ? 'bg-vexu/10 border border-vexu/20' : 'bg-vexu/15 border border-vexu/25' : isLight ? 'hover:bg-slate-100 active:bg-slate-200' : 'hover:bg-white/[0.04] active:bg-white/[0.08]'}
                              ${!inMonth ? 'opacity-20 cursor-default' : ''}
                            `} data-id="element-1181" data-mp="360ij3vk0ou7s" data-mpi={i}>
                            <span className={`text-[13px] ${isSelected ? 'text-white font-bold' : isToday ? isLight ? 'text-vexu-700 font-bold' : 'text-vexu font-bold' : isLight ? 'text-slate-700 font-medium' : 'text-zinc-300 font-medium'}`} data-id="element-1182" data-mp="d8n71vc6wq6y">
                              {inMonth ? day : ''}
                            </span>
                            {isToday && !isSelected && <div className={`w-1 h-1 rounded-full absolute bottom-1.5 ${isLight ? 'bg-vexu-600' : 'bg-vexu'}`} data-id="element-1183" data-mp="2esy0gs9bufb5" />}
                          </button>;
                })}
                    </div>
                  </div>

                  <div className="flex flex-col items-center justify-center text-center pt-8" data-id="element-1184" data-mp="1v3vuwyebklkj">
                    <p className={`text-sm font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1185" data-mp="202z1wodv11v5">
                      No events this month
                    </p>
                    <p className={`text-xs leading-relaxed max-w-[220px] mb-5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1186" data-mp="1r1shi483b6wm">
                      Events booked by Vexu.ai or from your calendar will appear
                      here.
                    </p>
                    <button onClick={() => setHasCalendarData(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-vexu text-white text-sm font-medium hover:bg-vexu-600 transition-colors" data-id="element-1187" data-mp="3j26g29aih0fm">
                      <Zap className="w-4 h-4" data-id="element-1188" data-mp="351vm2xdwn5q8" />
                      Simulate Events
                    </button>
                  </div>
                </div>}
            </motion.div> : view === 'day' ? <motion.div key="dayView" initial={{
          opacity: 0,
          x: -12
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 12
        }} transition={{
          duration: 0.25
        }} className="space-y-3" data-id="element-1189" data-mp="1kwxc9ziqt5f">
              {(EVENTS.some(e => e.day === selectedDate) ? EVENTS.filter(e => e.day === selectedDate) : EVENTS.filter(e => e.day === TODAY_DAY)).map((event, i) => <motion.button key={event.id} onClick={() => setSelectedEvent(event)} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.06,
            duration: 0.3
          }} className={`w-full text-left backdrop-blur-xl border rounded-2xl p-4 relative overflow-hidden group transition-all active:scale-[0.99] ${isLight ? 'bg-white/80 border-slate-200/60 shadow-sm hover:bg-white hover:shadow-md' : 'bg-white/[0.03] border-white/[0.07] shadow-xl hover:bg-white/[0.05]'}`} data-id="element-1190" data-mp="11td8rc6otzkh" data-mpi={i}>
                  {event.aiManaged && <div className={`absolute top-0 right-0 text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-bl-xl border-b border-l ${isLight ? 'bg-vexu/10 text-vexu-700 border-vexu/20' : 'bg-vexu/10 text-vexu border-vexu/15'}`} data-id="element-1191" data-mp="2w77ke9atqpzu">
                      AI Scheduled
                    </div>}
                  <div className="flex items-start gap-3.5" data-id="element-1192" data-mp="2d94odajuzssj">
                    <div className={`w-1 h-14 rounded-full mt-0.5 ${event.accent} opacity-80`} data-id="element-1193" data-mp="2dbqx3dihkecc" />
                    <div className="flex-1 min-w-0" data-id="element-1194" data-mp="2maugyjph8xrw">
                      <h3 className={`font-semibold text-[15px] mb-1.5 truncate pr-16 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1195" data-mp="2m8890iyocg95">
                        {event.title}
                      </h3>
                      <div className={`flex items-center gap-1.5 text-xs mb-3 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1196" data-mp="3sqwr32qteikf">
                        <Clock className="w-3.5 h-3.5 shrink-0" data-id="element-1197" data-mp="93msrpos77fe" />
                        <span data-id="element-1198" data-mp="94xu7iur833c">{event.time}</span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1199" data-mp="3ra9ex3889yin">
                        <div className={`flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-lg border truncate ${isLight ? 'bg-slate-50 border-slate-200 text-slate-600' : 'bg-white/[0.04] border-white/[0.06] text-zinc-300'}`} data-id="element-1200" data-mp="2ok2u3eebqh">
                          {event.type === 'video' ? <Video className="w-3 h-3 shrink-0" data-id="element-1201" data-mp="e26bzibzq542" /> : <Phone className="w-3 h-3 shrink-0" data-id="element-1202" data-mp="j07xsb0swij8" />}
                          <span className="truncate" data-id="element-1203" data-mp="16pshwx8oxlu3">{event.location}</span>
                        </div>
                        <div className="flex -space-x-1.5" data-id="element-1204" data-mp="1bjoatla3kiie">
                          {event.attendees.slice(0, 3).map((att, j) => <div key={j} className={`w-6 h-6 rounded-full border flex items-center justify-center text-[9px] font-bold ${isLight ? 'bg-slate-100 border-white text-slate-600' : 'bg-white/[0.06] border-dark text-zinc-400'}`} data-id="element-1205" data-mp="1gezebpblwe0e" data-mpi={j}>
                              {att.charAt(0)}
                            </div>)}
                          {event.attendees.length > 3 && <div className={`w-6 h-6 rounded-full border flex items-center justify-center text-[9px] font-bold ${isLight ? 'bg-slate-100 border-white text-slate-500' : 'bg-white/[0.06] border-dark text-zinc-500'}`} data-id="element-1206" data-mp="spejo963bsba">
                              +{event.attendees.length - 3}
                            </div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.button>)}
            </motion.div> : <motion.div key="monthView" initial={{
          opacity: 0,
          x: 12
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -12
        }} transition={{
          duration: 0.25
        }} data-id="element-1207" data-mp="2zdnjij0l4yiz">
              <div className={`backdrop-blur-xl border rounded-2xl p-4 ${isLight ? 'bg-white/80 border-slate-200/60 shadow-sm' : 'bg-white/[0.03] border-white/[0.07]'}`} data-id="element-1208" data-mp="2gmxhsxmr3hrp">
                <div className="grid grid-cols-7 gap-0.5 mb-1" data-id="element-1209" data-mp="1nz9zxqcv758r">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} className={`text-center text-[11px] font-bold py-2 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1210" data-mp="1svs3wo0dezk1" data-mpi={i}>
                      {d}
                    </div>)}
                </div>
                <div className="grid grid-cols-7 gap-0.5" data-id="element-1211" data-mp="vcieqjagzybi">
                  {Array.from({
                length: 35
              }).map((_, i) => {
                const day = i + 1;
                const inMonth = day >= 1 && day <= 28;
                const isToday = day === TODAY_DAY;
                const isSelected = day === selectedDate && inMonth;
                const hasEvent = EVENT_DAYS.includes(day);
                return <button key={i} onClick={() => inMonth && setSelectedDate(day)} disabled={!inMonth} className={`
                          aspect-square flex flex-col items-center justify-center rounded-xl relative cursor-pointer transition-all
                          ${isSelected ? 'bg-vexu text-white shadow-[0_4px_12px_rgba(0,200,83,0.3)] scale-[1.02]' : isToday ? isLight ? 'bg-vexu/10 border border-vexu/20' : 'bg-vexu/15 border border-vexu/25' : isLight ? 'hover:bg-slate-100 active:bg-slate-200' : 'hover:bg-white/[0.04] active:bg-white/[0.08]'}
                          ${!inMonth ? 'opacity-20 cursor-default' : ''}
                        `} data-id="element-1212" data-mp="1a0whyvbstosl" data-mpi={i}>
                        <span className={`text-[13px] ${isSelected ? 'text-white font-bold' : isToday ? isLight ? 'text-vexu-700 font-bold' : 'text-vexu font-bold' : isLight ? 'text-slate-700 font-medium' : 'text-zinc-300 font-medium'}`} data-id="element-1213" data-mp="1omtto8ip38j9">
                          {inMonth ? day : ''}
                        </span>
                        {hasEvent && inMonth && <div className={`w-1 h-1 rounded-full absolute bottom-1.5 ${isSelected ? 'bg-white' : isToday ? isLight ? 'bg-vexu-600' : 'bg-vexu' : isLight ? 'bg-slate-400' : 'bg-zinc-500'}`} data-id="element-1214" data-mp="15ur9woarbc4s" />}
                        {isToday && !isSelected && !hasEvent && <div className={`w-1 h-1 rounded-full absolute bottom-1.5 ${isLight ? 'bg-vexu-600' : 'bg-vexu'}`} data-id="element-1215" data-mp="6tj11daaqqvs" />}
                      </button>;
              })}
                </div>
              </div>

              <div className="mt-5" data-id="element-1216" data-mp="kkp9vzzgu0vc">
                <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1217" data-mp="klquow6aa1q9">
                  Events on Feb {selectedDate}
                </h3>
                <div className="space-y-2.5" data-id="element-1218" data-mp="zihlih1w6549">
                  {EVENTS.slice(0, 2).map(event => <button key={event.id} onClick={() => setSelectedEvent(event)} className={`w-full text-left border rounded-xl p-3 flex items-center gap-3 transition-all active:scale-[0.99] ${isLight ? 'bg-white border-slate-200 shadow-sm hover:bg-slate-50' : 'bg-white/[0.03] border-white/[0.07] hover:bg-white/[0.05]'}`} data-id="element-1219" data-mp="2k0iofds592dg">
                      <div className={`w-1 h-10 rounded-full ${event.accent} opacity-80`} data-id="element-1220" data-mp="32rjl1v5skltu" />
                      <div className="flex-1 min-w-0" data-id="element-1221" data-mp="2xsfdf8yimw5m">
                        <h4 className={`text-sm font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1222" data-mp="2t0x8k9tdgbfd">
                          {event.title}
                        </h4>
                        <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1223" data-mp="3li9lld7w3wjj">
                          {event.time}
                        </p>
                      </div>
                      {event.aiManaged && <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md border ${isLight ? 'text-vexu-700 bg-vexu/10 border-vexu/20' : 'text-vexu bg-vexu/10 border-vexu/15'}`} data-id="element-1224" data-mp="1jramvga5cqfi">
                          AI
                        </span>}
                    </button>)}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
  return <div className="h-full flex flex-col relative overflow-hidden" data-id="element-1225" data-mp="m64u4d2onolx">
      {/* Background orbs */}
      <div className={`absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/10' : 'bg-vexu/[0.04]'}`} data-id="element-1226" data-mp="r46kzk6cos7y" />
      <div className={`absolute bottom-32 -left-20 w-64 h-64 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-blue-500/10' : 'bg-blue-500/[0.03]'}`} data-id="element-1227" data-mp="2li7jg3xznd7i" />

      <AnimatePresence mode="wait" data-id="element-1228" data-mp="4v9c8v9844xt">
        {connectionState === 'disconnected' && renderDisconnected()}
        {connectionState === 'connecting' && renderConnecting()}
        {connectionState === 'success' && renderSuccess()}
        {connectionState === 'error' && renderError()}
        {connectionState === 'connected' && renderConnected()}
      </AnimatePresence>

      {/* Reminder Toast */}
      <AnimatePresence data-id="element-1229" data-mp="nlqzwlf78acb">
        {reminderToast && <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        type: 'spring',
        stiffness: 350,
        damping: 28
      }} className="absolute bottom-28 left-4 right-4 z-[70] flex justify-center" data-id="element-1230" data-mp="nl7id5tgnnyt">
            <div className={`flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl max-w-[320px] ${isLight ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'}`} data-id="element-1231" data-mp="1vcmui8tq0azz">
              <Bell className="w-4 h-4 text-vexu flex-shrink-0" data-id="element-1232" data-mp="24edz1aciybs9" />
              <span className="text-xs font-medium leading-snug" data-id="element-1233" data-mp="17ntzqfou496y">
                {reminderToast}
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Event Detail Sheet */}
      <AnimatePresence data-id="element-1234" data-mp="1go9y5aqq0q4y">
        {selectedEvent && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="absolute inset-0 z-50 flex items-end justify-center" data-id="element-1235" data-mp="1gmfd7wfg8y0v">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedEvent(null)} data-id="element-1236" data-mp="le4yioteq4ar" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 32
        }} className={`relative w-full max-h-[88%] rounded-t-3xl overflow-hidden flex flex-col ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border-t border-white/10'}`} data-id="element-1237" data-mp="cehz28k8n8kc">
              {/* Drag handle */}
              <div className="pt-2 pb-1 flex justify-center flex-shrink-0" data-id="element-1238" data-mp="3fvgx5mkikxht">
                <div className={`w-10 h-1 rounded-full ${isLight ? 'bg-slate-200' : 'bg-white/15'}`} data-id="element-1239" data-mp="3fxjlm260qikf" />
              </div>

              {/* Accent header strip */}
              <div className="px-6 pt-3 pb-5 flex-shrink-0" data-id="element-1240" data-mp="31auo25vbufgo">
                <div className="flex items-start justify-between gap-3 mb-4" data-id="element-1241" data-mp="368w56l9704pi">
                  <div className="flex items-start gap-3 min-w-0" data-id="element-1242" data-mp="2rb3rzzr4bseh">
                    <div className={`w-1.5 h-12 rounded-full flex-shrink-0 mt-0.5 ${selectedEvent.accent}`} data-id="element-1243" data-mp="2rcyh7q9fa6od" />
                    <div className="min-w-0" data-id="element-1244" data-mp="3k0arp22b6o1b">
                      <h2 className={`text-xl font-bold leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1245" data-mp="3k1lv30rudtrw">
                        {selectedEvent.title}
                      </h2>
                      <p className={`text-sm mt-1 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1246" data-mp="3f7rbryg5fgtc">
                        {selectedEvent.date}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedEvent(null)} className={`p-1.5 rounded-full flex-shrink-0 ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/5'}`} data-id="element-1247" data-mp="1w9v5aog2da59">
                    <X className="w-4 h-4" data-id="element-1248" data-mp="25z4wm2332osb" />
                  </button>
                </div>

                {selectedEvent.aiManaged && <div className="space-y-2.5" data-id="element-1249" data-mp="1dazbvfc14zsh">
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${isLight ? 'bg-vexu/10 border border-vexu/20' : 'bg-vexu/10 border border-vexu/20'}`} data-id="element-1250" data-mp="1dbs8hnh9y8ww">
                      <Bot className={`w-3.5 h-3.5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1251" data-mp="1m9kpqx6k3qmx" />
                      <span className={`text-xs font-semibold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1252" data-mp="2xqazgqa6v0la">
                        Scheduled by your AI agent
                      </span>
                    </div>
                    {selectedEvent.originatingCall && <button type="button" onClick={() => {
                onViewCall?.(selectedEvent.originatingCall!.id);
                setSelectedEvent(null);
              }} className={`flex w-full items-center justify-between rounded-xl border px-3.5 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300' : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20'}`} data-id="element-1253" data-mp="w2pz7qhznjjf">
                        <span className="flex items-center gap-2.5 min-w-0" data-id="element-1254" data-mp="1onzocvko2vxz"><span className={`w-8 h-8 rounded-lg grid place-items-center flex-shrink-0 ${isLight ? 'bg-white text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-1255" data-mp="3ljnpf53l10rx"><PhoneCall className="w-4 h-4" data-id="element-1256" data-mp="3qjt8hwvsdeye" /></span><span className="min-w-0" data-id="element-1257" data-mp="27n47j9k5vkny"><span className={`block text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1258" data-mp="27nm8uklokp2q">Originating call</span><span className={`mt-0.5 block truncate text-xs font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-1259" data-mp="22u1b8to01x42">{selectedEvent.originatingCall.label}</span></span></span>
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1260" data-mp="1tt37mchh3rm0" />
                      </button>}
                  </div>}
              </div>

              <div className={`flex-1 overflow-y-auto px-6 pb-6 space-y-5 border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-1261" data-mp="2hk8uhbltzk5t">
                {/* Time */}
                <div className="flex items-start gap-3.5 pt-5" data-id="element-1262" data-mp="1664rjfx9mlom">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1263" data-mp="163in3p5o8mam">
                    <Clock className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1264" data-mp="23hcnpvjewwm3" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1265" data-mp="1tiwy5x019vep">
                    <p className={`text-[11px] font-medium uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1266" data-mp="1tlrjrrus9m9p">
                      Time
                    </p>
                    <p className={`text-sm font-semibold mt-0.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1267" data-mp="1fs05k663xe7n">
                      {selectedEvent.time}
                    </p>
                    <p className={`text-xs mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1268" data-mp="15vf41utaql8k">
                      {selectedEvent.duration}
                    </p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-3.5" data-id="element-1269" data-mp="38fzj2ka5ezd2">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1270" data-mp="3cimf6xdur6g2">
                    {selectedEvent.type === 'video' ? <Video className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1271" data-mp="3mfr06tvyovva" /> : <Phone className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1272" data-mp="2trlh0bkk3exk" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1273" data-mp="33hoaecvlauwf">
                    <p className={`text-[11px] font-medium uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1274" data-mp="33jiy1yy8x147">
                      {selectedEvent.type === 'video' ? 'Video call' : 'Phone call'}
                    </p>
                    <p className={`text-sm font-semibold mt-0.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1275" data-mp="1yzc6fl14zngg">
                      {selectedEvent.location}
                    </p>
                    {selectedEvent.meetingLink && <a href="#" onClick={e => e.preventDefault()} className={`inline-flex items-center gap-1 text-xs font-medium mt-1 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1276" data-mp="27tqw8hjamgdt">
                        <Link2 className="w-3 h-3" data-id="element-1277" data-mp="1u1u80okqwqmp" />
                        {selectedEvent.meetingLink}
                      </a>}
                  </div>
                </div>

                {/* Organizer */}
                <div className="flex items-start gap-3.5" data-id="element-1278" data-mp="35h95ruks3miv">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1279" data-mp="30fjucdtn96u9">
                    <MapPin className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1280" data-mp="r8q11izu5pxf" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1281" data-mp="i6p6itvzlhzg">
                    <p className={`text-[11px] font-medium uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1282" data-mp="i7ig02mbylla">
                      Organizer
                    </p>
                    <p className={`text-sm font-semibold mt-0.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1283" data-mp="w6rlo8cwtxb0">
                      {selectedEvent.organizer}
                    </p>
                  </div>
                </div>

                {/* Attendees */}
                <div className="flex items-start gap-3.5" data-id="element-1284" data-mp="1tm5hdvkxzfq4">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1285" data-mp="1tnp15w5t48wn">
                    <Users className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1286" data-mp="ghugpqn47x" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1287" data-mp="3n6h5if4mmcad">
                    <p className={`text-[11px] font-medium uppercase tracking-wider mb-2 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1288" data-mp="3n7s6y8aln7yb">
                      {selectedEvent.attendees.length} Attendees
                    </p>
                    <div className="space-y-2" data-id="element-1289" data-mp="exgynp6e5erm">
                      {selectedEvent.attendees.map((att, idx) => <div key={idx} className="flex items-center gap-2.5" data-id="element-1290" data-mp="9yw5g9pk3vco" data-mpi={idx}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-1291" data-mp="17fuvpmjwkwpn">
                            {att.charAt(0)}
                          </div>
                          <span className={`text-sm ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1292" data-mp="1eyklzvr0w0z3">
                            {att}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="flex items-start gap-3.5" data-id="element-1293" data-mp="1xvlx7yb66xbh">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1294" data-mp="22nde1h4ixoof">
                    <FileText className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1295" data-mp="2bov0dh8iymlk" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1296" data-mp="2gllg23gf3g6g">
                    <p className={`text-[11px] font-medium uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1297" data-mp="gknamhur4pfo">
                      Notes
                    </p>
                    <p className={`text-sm leading-relaxed mt-1 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-1298" data-mp="qcsxjd65cgo0">
                      {selectedEvent.description}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2" data-id="element-1299" data-mp="va1krs65jaw4">
                  <button onClick={() => toggleReminder(selectedEvent)} className={`flex-1 py-3 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2 ${reminderIds.includes(selectedEvent.id) ? isLight ? 'bg-vexu/10 text-vexu-700 border border-vexu/20' : 'bg-vexu/15 text-vexu border border-vexu/20' : isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`} data-id="element-1300" data-mp="19am20jwf4yni">
                    {reminderIds.includes(selectedEvent.id) ? <>
                        <CheckCircle2 className="w-4 h-4" data-id="element-1301" data-mp="1csjgpt771lxi" />
                        Reminder set
                      </> : <>
                        <Bell className="w-4 h-4" data-id="element-1302" data-mp="12ttzm2n4g5ib" />
                        Remind me
                      </>}
                  </button>
                  {selectedEvent.type === 'video' && selectedEvent.meetingLink ? <button className="flex-1 py-3 rounded-xl font-medium text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors flex items-center justify-center gap-2" data-id="element-1303" data-mp="tuqhp1zoxw5e">
                      <Video className="w-4 h-4" data-id="element-1304" data-mp="2o888qyzb1b56" />
                      Join
                    </button> : selectedEvent.type === 'phone' ? <button className="flex-1 py-3 rounded-xl font-medium text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors flex items-center justify-center gap-2" data-id="element-1305" data-mp="1fzf2f0nabcg5">
                      <Phone className="w-4 h-4" data-id="element-1306" data-mp="1b2evyomiw7oi" />
                      Call
                    </button> : null}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Calendar Settings Sheet */}
      <AnimatePresence data-id="element-1307" data-mp="xa8ft2jde0p2">
        {showSettings && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="absolute inset-0 z-50 flex items-end justify-center" data-id="element-1308" data-mp="s9ksory9i404">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowSettings(false)} data-id="element-1309" data-mp="2cpo3apbycu7l" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 32
        }} className={`relative w-full max-h-[85%] rounded-t-3xl overflow-hidden flex flex-col ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border-t border-white/10'}`} data-id="element-1310" data-mp="2l66lq816ujas">
              {/* Drag handle */}
              <div className="pt-2 pb-1 flex justify-center" data-id="element-1311" data-mp="2b7qu83y9180r">
                <div className={`w-10 h-1 rounded-full ${isLight ? 'bg-slate-200' : 'bg-white/15'}`} data-id="element-1312" data-mp="2g7bv4jwzbqg4" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-3 pb-4 border-b ${isLight ? 'border-slate-100' : 'border-white/5'}" data-id="element-1313" data-mp="3rms2vcqn182k">
                <div data-id="element-1314" data-mp="1wycf6xj2npqa">
                  <h2 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1315" data-mp="1wxt3vznxfzl8">
                    Calendar accounts
                  </h2>
                  <p className={`text-xs mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1316" data-mp="1n6yh8u6rqxz9">
                    Manage your connected calendars
                  </p>
                </div>
                <button onClick={() => setShowSettings(false)} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/5'}`} data-id="element-1317" data-mp="1i8dhevvfigbc">
                  <X className="w-4 h-4" data-id="element-1318" data-mp="196xaiy3towbv" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-1319" data-mp="2ym3qyi5bhwsd">
                {/* Connected accounts */}
                {connectedAccounts.length > 0 && <div className="mb-6" data-id="element-1320" data-mp="33jdm8efe12o4">
                    <p className={`text-[11px] font-bold uppercase tracking-wider mb-2.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1321" data-mp="3dovdxehpjaap">
                      Connected ({connectedAccounts.length})
                    </p>
                    <div className="space-y-2" data-id="element-1322" data-mp="3hr9utt1tfzzc">
                      {connectedAccounts.map(acc => <div key={acc.id} className={`flex items-center gap-3 p-3 rounded-2xl border ${isLight ? 'bg-white border-slate-200' : 'bg-white/[0.03] border-white/[0.08]'}`} data-id="element-1323" data-mp="ai8cqdhk6x2">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-vexu/10' : 'bg-vexu/15'}`} data-id="element-1324" data-mp="9aneyyauv1a6">
                            {acc.provider === 'phone' ? <Smartphone className={`w-5 h-5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1325" data-mp="2729kwwd57mqm" /> : acc.provider === 'icloud' ? <Cloud className={`w-5 h-5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1326" data-mp="f1rfh100vx5v" /> : <Mail className={`w-5 h-5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1327" data-mp="ovrrznxmw4om" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1328" data-mp="12p99z4ao99cj">
                            <div className="flex items-center gap-1.5" data-id="element-1329" data-mp="12pre0n3ksrv0">
                              <p className={`text-sm font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1330" data-mp="17natfqc0faoj">
                                {acc.label}
                              </p>
                              <CheckCircle2 className="w-3.5 h-3.5 text-vexu flex-shrink-0" data-id="element-1331" data-mp="3172yxj1i9ynd" />
                            </div>
                            <p className={`text-xs truncate mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1332" data-mp="2x57zyl71rxcr">
                              {acc.detail}
                            </p>
                          </div>
                          <button onClick={() => handleRemoveAccount(acc.id)} className={`p-2 rounded-full flex-shrink-0 ${isLight ? 'text-red-500 hover:bg-red-50' : 'text-red-400 hover:bg-red-500/10'}`} data-id="element-1333" data-mp="3kvk6psqk3o1r">
                            <Trash2 className="w-4 h-4" data-id="element-1334" data-mp="366fnq9bxtfsl" />
                          </button>
                        </div>)}
                    </div>
                  </div>}

                {/* Add another account */}
                <div data-id="element-1335" data-mp="2nuta65vksi2">
                  <p className={`text-[11px] font-bold uppercase tracking-wider mb-2.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1336" data-mp="2nmda5egh72n">
                    {connectedAccounts.length > 0 ? 'Add another' : 'Available providers'}
                  </p>
                  <div className="space-y-2" data-id="element-1337" data-mp="3ed7cklpm89w2">
                    {ALL_PROVIDERS.filter(p => !connectedAccounts.some(a => a.provider === p.id)).map(p => {
                  const isAdding = addingProvider === p.id;
                  return <button key={p.id} onClick={() => !isAdding && handleAddAccount(p.id)} disabled={isAdding} className={`w-full flex items-center gap-3 p-3 rounded-2xl border text-left transition-colors ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 active:bg-slate-100' : 'bg-white/[0.03] border-white/[0.08] hover:bg-white/[0.06]'}`} data-id="element-1338" data-mp="3scp6ffhrqdca">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1339" data-mp="29flr3y45snl8">
                            {p.id === 'phone' ? <Smartphone className={`w-5 h-5 ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-1340" data-mp="1vf1c7d1d7bx0" /> : p.id === 'icloud' ? <Cloud className={`w-5 h-5 ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-1341" data-mp="24fzf1s02hde0" /> : <Mail className={`w-5 h-5 ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-1342" data-mp="3ftuwx8icw08x" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1343" data-mp="28i3w6bjgcbi6">
                            <p className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1344" data-mp="2dgovm8qvqr5k">
                              {p.label}
                            </p>
                            <p className={`text-xs mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1345" data-mp="2mhd992cd2phx">
                              {isAdding ? 'Connecting...' : p.detail}
                            </p>
                          </div>
                          {isAdding ? <Loader2 className={`w-4 h-4 animate-spin flex-shrink-0 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1346" data-mp="23ju07ypwl6vi" /> : <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-1347" data-mp="1b1y4vdhtovcs">
                              <Plus className="w-4 h-4" data-id="element-1348" data-mp="yiaugm64umz7" />
                            </div>}
                        </button>;
                })}
                    {ALL_PROVIDERS.every(p => connectedAccounts.some(a => a.provider === p.id)) && <div className={`text-center text-xs py-6 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1349" data-mp="ftmduceon3t8">
                        All available calendar providers are connected.
                      </div>}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}